import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { 
  Wifi, 
  Search, 
  RefreshCw, 
  Users, 
  Clock,
  ArrowUpCircle,
  ArrowDownCircle,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Monitor,
  Globe,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format bytes to human readable
const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const HotspotOnline = () => {
  const { token, selectedBranch } = useAuth();
  const { selectedRouter } = useApp();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalSessions, setTotalSessions] = useState(0);
  
  // Pagination - default 10
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [liveMode, setLiveMode] = useState(true);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const getSortValue = (session, field) => {
    switch (field) {
      case 'username': return (session.username || '').toLowerCase();
      case 'router':   return (session.router_name || '').toLowerCase();
      case 'mac':      return (session.mac_address || '').toLowerCase();
      case 'ip':       {
        const ip = session.ip_address || '';
        const m = ip.match(/^(\d+)\.(\d+)\.(\d+)\.(\d+)/);
        if (m) return (+m[1]<<24) + (+m[2]<<16) + (+m[3]<<8) + (+m[4]);
        return ip;
      }
      case 'uptime':   return Number(session.uptime_seconds || session.acctsessiontime || 0);
      case 'owner':    return (session.owner_name || '').toLowerCase();
      case 'upload':   return Number(session.upload_bytes || (session.upload_mb ? session.upload_mb * 1024 * 1024 : 0));
      case 'download': return Number(session.download_bytes || (session.download_mb ? session.download_mb * 1024 * 1024 : 0));
      default:         return '';
    }
  };

  const displaySessions = React.useMemo(() => {
    if (!sortField) return sessions;
    const arr = [...sessions];
    arr.sort((a, b) => {
      const va = getSortValue(a, sortField);
      const vb = getSortValue(b, sortField);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [sessions, sortField, sortDir]);

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 opacity-40" />;
    return sortDir === 'asc' ? <ArrowUp className="w-3 h-3 text-indigo-600 dark:text-indigo-400" /> : <ArrowDown className="w-3 h-3 text-indigo-600 dark:text-indigo-400" />;
  };
  const SortableTh = ({ field, children, className }) => (
    <th
      onClick={() => toggleSort(field)}
      className={`px-3 py-3 text-left font-semibold text-slate-600 cursor-pointer select-none hover:text-indigo-600 transition-colors ${className || ''}`}
      data-testid={`hs-th-sort-${field}`}
    >
      <span className="inline-flex items-center gap-1.5">
        {children}
        <SortIcon field={field} />
      </span>
    </th>
  );

  const fetchSessions = useCallback(async (showLoading = true) => {
    if (!token) return;
    
    if (showLoading) setLoading(true);
    else setRefreshing(true);
    
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      if (liveMode) {
        params.append('live', '1');
      }
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      // Add branch filter if selected
      if (selectedBranch && selectedBranch !== 'all') {
        params.append('branch_id', selectedBranch);
      }
      // Add router filter from header selector
      if (selectedRouter && selectedRouter !== 'all') {
        params.append('router_id', selectedRouter);
      }
      
      const response = await axios.get(
        `${API_URL}/api/radius/hotspot-sessions?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const data = response.data.sessions || [];
        setSessions(data);
        const _total = response.data.total || 0;
        setTotalSessions(_total);
        setTotalPages(response.data.total_pages || Math.max(1, Math.ceil(_total / limit)));
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast.error('Gagal memuat data session');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, page, limit, searchTerm, selectedBranch, selectedRouter, liveMode]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);
  
  // Reset to page 1 when branch changes
  useEffect(() => {
    setPage(1);
  }, [selectedBranch, selectedRouter]);

  const handleRefresh = () => {
    fetchSessions(false);
    toast.success('Data diperbarui');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchSessions();
  };

  const handleDisconnect = async (session) => {
    if (!window.confirm(`Disconnect user ${session.username}?`)) return;
    
    try {
      const response = session.live
        ? await axios.post(
            `${API_URL}/api/radius/hotspot-sessions/disconnect-live`,
            { router_id: session.router_id, session_id: session.ros_id },
            { headers: { Authorization: `Bearer ${token}` } }
          )
        : await axios.post(
            `${API_URL}/api/hotspot/disconnect/${session.username}`,
            { router_ip: session.router_nas_ip },
            { headers: { Authorization: `Bearer ${token}` } }
          );
      
      if (response.data.success) {
        toast.success('Session berhasil di-disconnect');
        fetchSessions(false);
      } else {
        toast.error(response.data.message || 'Gagal disconnect');
      }
    } catch (error) {
      console.error('Error disconnecting:', error);
      toast.error('Gagal disconnect session');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-4 md:p-6 max-w-full" data-testid="hotspot-online-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-800 flex items-center gap-2 dark:text-slate-200">
            <Wifi className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            Hotspot Online
          </h1>
          <p className="text-sm text-slate-600 mt-1 dark:text-slate-300">
            {liveMode
              ? 'Session Hotspot LIVE langsung dari MikroTik (termasuk login mac-cookie)'
              : 'Session Hotspot aktif dari RADIUS database'}
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setLiveMode(prev => !prev)}
            data-testid="live-mode-toggle"
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition ${
              liveMode
                ? 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700'
                : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-600'
            }`}
          >
            <Globe className="w-4 h-4" />
            {liveMode ? 'Live Router' : 'RADIUS DB'}
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-btn"
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-xl shadow-sm border p-4 dark:bg-slate-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg dark:bg-purple-900/40">
              <Users className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400">Total Online</p>
              <p className="text-xl font-bold text-slate-800 dark:text-slate-200">{totalSessions}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border p-4 dark:bg-slate-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg dark:bg-green-900/40">
              <Globe className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400">Halaman</p>
              <p className="text-xl font-bold text-slate-800 dark:text-slate-200">{page}/{totalPages || 1}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border p-4 dark:bg-slate-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg dark:bg-blue-900/40">
              <Monitor className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400">Ditampilkan</p>
              <p className="text-xl font-bold text-slate-800 dark:text-slate-200">{sessions.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-sm border p-4 dark:bg-slate-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg dark:bg-orange-900/40">
              <Clock className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <p className="text-xs text-slate-500 dark:text-slate-400">Update</p>
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                {new Date().toLocaleTimeString('id-ID')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm border p-4 mb-6 dark:bg-slate-800">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
            <input
              type="text"
              placeholder="Cari username atau MAC..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="search-input"
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
            />
          </div>
          
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          
          <button
            type="submit"
            data-testid="search-btn"
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
          >
            Cari
          </button>
        </form>
      </div>

      {/* Sessions Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden dark:bg-slate-800">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b dark:bg-slate-900">
              <tr>
                <SortableTh field="username">USERNAME</SortableTh>
                <SortableTh field="router">ROUTER</SortableTh>
                <SortableTh field="mac">MAC</SortableTh>
                <SortableTh field="ip">IP</SortableTh>
                <SortableTh field="uptime">UPTIME</SortableTh>
                <SortableTh field="owner">OWNER</SortableTh>
                <SortableTh field="upload">
                  <span className="inline-flex items-center gap-1">
                    <ArrowUpCircle className="w-3 h-3 text-green-500" />
                    UPLOAD
                  </span>
                </SortableTh>
                <SortableTh field="download">
                  <span className="inline-flex items-center gap-1">
                    <ArrowDownCircle className="w-3 h-3 text-blue-500" />
                    DOWNLOAD
                  </span>
                </SortableTh>
                <th className="px-3 py-3 text-center font-semibold text-slate-600 dark:text-slate-300">AKSI</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan="9" className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <RefreshCw className="w-8 h-8 text-purple-500 animate-spin mb-2" />
                      <p className="text-slate-500 dark:text-slate-400">Memuat data...</p>
                    </div>
                  </td>
                </tr>
              ) : sessions.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <Wifi className="w-12 h-12 text-slate-300 mb-2" />
                      <p className="text-slate-500 dark:text-slate-400">Tidak ada session hotspot aktif</p>
                    </div>
                  </td>
                </tr>
              ) : (
                displaySessions.map((session, index) => (
                  <tr 
                    key={session.id || session.session_id || index} 
                    className="hover:bg-slate-50 dark:hover:bg-slate-700/50"
                    data-testid={`session-row-${index}`}
                  >
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                        <span className="font-medium text-purple-600 dark:text-purple-400">{session.username}</span>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-sm text-slate-600 dark:text-slate-300">
                      {session.router_name || '-'}
                    </td>
                    <td className="px-3 py-3 font-mono text-xs text-slate-500 dark:text-slate-400">
                      {session.mac_address || '-'}
                    </td>
                    <td className="px-3 py-3 font-mono text-xs text-slate-700 dark:text-slate-200">
                      {session.ip_address || '-'}
                    </td>
                    <td className="px-3 py-3">
                      <span className="font-mono text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded dark:bg-purple-900/30 dark:text-purple-300">
                        {session.uptime || '-'}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-sm text-slate-600 dark:text-slate-300">
                      {session.owner_name || '-'}
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-xs font-medium text-green-600 dark:text-green-400">
                        {session.upload_mb ? `${session.upload_mb} MB` : (session.upload || formatBytes(session.upload_bytes) || '0 B')}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <span className="text-xs font-medium text-blue-600 dark:text-blue-400">
                        {session.download_mb ? `${session.download_mb} MB` : (session.download || formatBytes(session.download_bytes) || '0 B')}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <button
                        onClick={() => handleDisconnect(session)}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition dark:hover:bg-red-900/30"
                        title="Disconnect"
                        data-testid={`disconnect-btn-${index}`}
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50 dark:bg-slate-900">
            <div className="text-xs text-slate-600 dark:text-slate-300">
              {((page - 1) * limit) + 1} - {Math.min(page * limit, totalSessions)} dari {totalSessions}
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 border rounded-lg hover:bg-white disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              
              <span className="px-3 py-1 bg-purple-600 text-white rounded-lg text-sm">
                {page}
              </span>
              
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 border rounded-lg hover:bg-white disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Info Note */}
      <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg dark:bg-purple-900/30">
        <p className="text-xs text-purple-700 dark:text-purple-300">
          <strong>Info:</strong> Data session Hotspot aktif dari RADIUS database. Klik tombol Refresh untuk memperbarui data.
        </p>
      </div>
    </div>
  );
};

export default HotspotOnline;
